import { useState, useEffect, useRef, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

export function useDialer(identity = "agent") {
  const deviceRef = useRef(null);
  const callRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | connecting | ringing | in-call | error
  const [device, setDevice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const timerRef = useRef(null);

  // ── Init Device ────────────────────────────────────────────────────────────
  useEffect(() => {
    let dev;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/token?identity=${identity}`);
        const { token } = await res.json();

        dev = new Device(token, {
          logLevel: 1,
          codecPreferences: ["opus", "pcmu"],
          fakeLocalDTMF: true,
          enableIceRestart: true,
        });

        dev.on("ready", () => { setIsReady(true); setStatus("idle"); });
        dev.on("error", (err) => { setError(err.message); setStatus("error"); });
        dev.on("registered", () => setIsReady(true));
        dev.on("unregistered", () => setIsReady(false));

        dev.on("incoming", (call) => {
          setIncomingCall(call);
          call.on("cancel", () => setIncomingCall(null));
        });

        await dev.register();
        deviceRef.current = dev;
        setDevice(dev);
      } catch (err) {
        setError("Failed to initialize device: " + err.message);
        setStatus("error");
      }
    })();

    return () => { dev?.destroy(); };
  }, [identity]);

  // ── Call Timer ─────────────────────────────────────────────────────────────
  const startTimer = useCallback(() => {
    setCallDuration(0);
    timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    setCallDuration(0);
  }, []);

  // ── Bind Call Events ───────────────────────────────────────────────────────
  const bindCallEvents = useCallback((call) => {
    call.on("accept", () => { setStatus("in-call"); startTimer(); });
    call.on("disconnect", () => {
      setStatus("idle");
      setActiveCall(null);
      setIsMuted(false);
      setIsOnHold(false);
      callRef.current = null;
      stopTimer();
    });
    call.on("cancel", () => {
      setStatus("idle");
      setActiveCall(null);
      callRef.current = null;
      stopTimer();
    });
    call.on("error", (err) => {
      setError(err.message);
      setStatus("error");
    });
    callRef.current = call;
    setActiveCall(call);
  }, [startTimer, stopTimer]);

  // ── Dial ───────────────────────────────────────────────────────────────────
  const dial = useCallback(async (number) => {
    if (!deviceRef.current || !number) return;
    setStatus("connecting");
    setError(null);
    try {
      const call = await deviceRef.current.connect({
        params: { To: number },
      });
      setStatus("ringing");
      bindCallEvents(call);
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  }, [bindCallEvents]);

  // ── Hangup ─────────────────────────────────────────────────────────────────
  const hangup = useCallback(() => {
    callRef.current?.disconnect();
    deviceRef.current?.disconnectAll();
  }, []);

  // ── Accept Incoming ────────────────────────────────────────────────────────
  const accept = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.accept();
    bindCallEvents(incomingCall);
    setIncomingCall(null);
    setStatus("in-call");
    startTimer();
  }, [incomingCall, bindCallEvents, startTimer]);

  // ── Reject Incoming ────────────────────────────────────────────────────────
  const reject = useCallback(() => {
    incomingCall?.reject();
    setIncomingCall(null);
  }, [incomingCall]);

  // ── Mute ───────────────────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !isMuted;
    call.mute(next);
    setIsMuted(next);
  }, [isMuted]);

  // ── Hold (simulated via mute + notify) ────────────────────────────────────
  const toggleHold = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !isOnHold;
    call.mute(next);
    setIsOnHold(next);
    if (!next) setIsMuted(false);
  }, [isOnHold]);

  // ── DTMF ───────────────────────────────────────────────────────────────────
  const sendDTMF = useCallback((digit) => {
    callRef.current?.sendDigits(digit);
  }, []);

  // ── Format duration ────────────────────────────────────────────────────────
  const formatDuration = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return {
    status,
    isReady,
    activeCall,
    incomingCall,
    isMuted,
    isOnHold,
    callDuration,
    formattedDuration: formatDuration(callDuration),
    error,
    dial,
    hangup,
    accept,
    reject,
    toggleMute,
    toggleHold,
    sendDTMF,
  };
}
